// In-app documentation reader.
// Renders the markdown files from docs/dashboards/ (synced into public/docs/
// by deploy.sh), organised by the same question sections as the sidebar.
import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const DOC_SECTIONS = [
  {
    section: 'Panel Statistics',
    docs: [
      { file: '00_panel_representativeness.md', title: 'Panel Representativeness' },
      { file: '01_panel_size.md',               title: 'Panel Size Statistics' },
    ],
  },
  {
    section: 'How is the category growing?',
    docs: [
      { file: '02_category_behaviour.md',        title: 'Category Behaviour' },
      { file: '03_trends_by_age_band.md',        title: 'Monthly Trends — By Age Band' },
      { file: '04_trends_by_gender.md',          title: 'Monthly Trends — By Gender' },
      { file: '05_trends_by_geography.md',       title: 'Monthly Trends — By Geography' },
      { file: '06_trends_intensity.md',          title: 'Monthly Trends — Intensity' },
      { file: '07_category_penetration_geo.md',  title: 'Category Penetration — By Geo' },
      { file: '08_category_penetration_age.md',  title: 'Category Penetration — By Age Band' },
      { file: '09_category_penetration_gender.md', title: 'Category Penetration — By Gender' },
    ],
  },
  {
    section: 'How is your brand growing?',
    docs: [
      { file: '10_share_and_penetration.md',   title: 'Share and Penetration' },
      { file: '11_trends_nation_topline.md',   title: 'Nation Topline' },
      { file: '12_brand_penetration_geo.md',   title: 'Brand Penetration — By Geo' },
      { file: '13_brand_penetration_age.md',   title: 'Brand Penetration — By Age Band' },
      { file: '14_brand_penetration_gender.md', title: 'Brand Penetration — By Gender' },
    ],
  },
  {
    section: 'Who is your competition?',
    docs: [
      { file: '15_customer_profiles_age.md',       title: 'Demographics — Age' },
      { file: '16_customer_profiles_gender.md',    title: 'Demographics — Gender' },
      { file: '17_customer_profiles_intensity.md', title: 'Intensity' },
      { file: '18_customer_profiles_geography.md', title: 'Geography' },
      { file: '19_app_loyalty.md',                 title: 'App Loyalty' },
      { file: '20_duplication_matrix.md',          title: 'Duplication Matrix' },
      { file: '21_share_of_requirements.md',       title: 'Share of Requirements' },
    ],
  },
  {
    section: 'What are your Growth Opportunities?',
    docs: [
      { file: '22_category_target_audience.md', title: 'Who are the Category Target Audience?' },
      { file: '23_brand_target_audience.md',    title: 'Who are my Brand Target Audience?' },
      { file: '24_penetration_targets.md',      title: 'Penetration Targets' },
    ],
  },
  {
    section: 'Identify Channel Preferences',
    docs: [
      { file: '25_social_channel_reach.md',        title: 'Social Channel Reach' },
      { file: '26_opportunity_index_by_channel.md', title: 'Opportunity Index by Channel' },
    ],
  },
  {
    section: 'Location Intelligence',
    docs: [
      { file: '27_postcode_intelligence.md', title: 'Postcode Intelligence' },
      { file: '28_pinpoint_intelligence.md', title: 'Pinpoint Intelligence' },
    ],
  },
  {
    section: 'Omni - Cross Category',
    docs: [
      { file: '29_football_affinity.md',   title: 'Football Affinity' },
      { file: '30_theme_park_affinity.md', title: 'Theme Parks Affinity' },
    ],
  },
  {
    section: 'Media Budget Allocation',
    docs: [
      { file: '31_media_budget_brand.md',     title: 'Based on Brand Behavior' },
      { file: '32_media_budget_intensity.md', title: 'Based on Category Intensity' },
    ],
  },
  {
    section: 'Reference',
    docs: [
      { file: 'README.md',                  title: 'Index & Design Principles' },
      { file: '15_postcode_reference.md',   title: 'UK Postcode Area Reference' },
    ],
  },
];

const mdCache = {};

export default function DocsView() {
  const [selected, setSelected] = useState(DOC_SECTIONS[0].docs[0]);
  const [content,  setContent]  = useState('');
  const [error,    setError]    = useState(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    if (mdCache[selected.file]) { setContent(mdCache[selected.file]); return; }
    setContent('');
    fetch(`/docs/${selected.file}`)
      .then(r => { if (!r.ok) throw new Error(`Failed to load (${r.status})`); return r.text(); })
      .then(text => { mdCache[selected.file] = text; if (!cancelled) setContent(text); })
      .catch(e => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [selected]);

  return (
    <div className="docs-layout">
      <div className="docs-nav">
        {DOC_SECTIONS.map(sec => (
          <div key={sec.section}>
            <div className="docs-nav-section">{sec.section}</div>
            {sec.docs.map(d => (
              <button
                key={`${sec.section}-${d.file}`}
                className={`docs-nav-item ${selected === d ? 'active' : ''}`}
                onClick={() => setSelected(d)}
              >
                {d.title}
              </button>
            ))}
          </div>
        ))}
      </div>

      <div className="docs-content">
        {error && <div className="empty-state">Could not load document: {error}</div>}
        {!error && !content && <div className="loading-state"><div className="spinner" /> Loading…</div>}
        {!error && content && (
          <div className="docs-md">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}
